/**
 * Routes, and the two states that sit in front of them.
 *
 * Nothing renders until the cold-load refresh has answered, because rendering
 * the login screen to somebody who already has a valid session would be a lie —
 * and the router would immediately replace it.
 */
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { Card, Spinner } from '@wend/ui';

import { AuthProvider, useAdminAuth } from './auth/AuthProvider';
import { RequireAdmin } from './auth/RequireAdmin';
import { ErrorState } from './components/ErrorState';
import { InterestDetailPage } from './routes/InterestDetailPage';
import { InterestsPage } from './routes/InterestsPage';
import { LoginPage } from './routes/LoginPage';
import { NewInterestPage } from './routes/NewInterestPage';
import { NewTenantPage } from './routes/NewTenantPage';
import { TenantsPage } from './routes/TenantsPage';
import { TenantDetailPage } from './routes/tenant-detail/TenantDetailPage';

function FullPage({ children }: { children: ReactNode }) {
  return (
    <div className="relative min-h-dvh bg-ink text-text">
      <div className="wend-ambient" aria-hidden="true" />
      <div className="relative z-10 mx-auto flex min-h-dvh w-full max-w-[26rem] flex-col justify-center px-pad py-12">
        {children}
      </div>
    </div>
  );
}

function AuthGate() {
  const { status, bootstrapError, retryBootstrap } = useAdminAuth();

  if (status === 'checking') {
    return (
      <FullPage>
        <div className="flex flex-col items-center gap-3 text-muted">
          {/* The text below already announces this, so the spinner is decorative. */}
          <Spinner size="lg" label={null} />
          <p role="status" className="font-mono text-[0.7rem] uppercase tracking-[0.12em] text-muted">
            Restoring your session
          </p>
        </div>
      </FullPage>
    );
  }

  if (status === 'error' && bootstrapError !== null) {
    return (
      <FullPage>
        <Card>
          <ErrorState
            error={bootstrapError}
            title="Cannot start"
            onRetry={retryBootstrap}
          />
        </Card>
      </FullPage>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<RequireAdmin />}>
        <Route path="/tenants" element={<TenantsPage />} />
        {/*
          `new` is declared before `:tenantId` so the literal wins. React Router
          v7 ranks static segments above dynamic ones and would get this right
          either way, but the ordering is written to be read: a route file where
          the two are the other way round invites the next person to "tidy" them
          into a list where /tenants/new resolves to a tenant whose id is "new".
        */}
        <Route path="/tenants/new" element={<NewTenantPage />} />
        <Route path="/tenants/:tenantId" element={<TenantDetailPage />} />

        {/* The sales desk. `new` before `:interestId`, for the reason above. */}
        <Route path="/interests" element={<InterestsPage />} />
        <Route path="/interests/new" element={<NewInterestPage />} />
        <Route path="/interests/:interestId" element={<InterestDetailPage />} />
      </Route>

      {/* One destination; anything else lands there. */}
      <Route path="*" element={<Navigate to="/tenants" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}
